import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  AprimaCacheService,
} from 'webcommon/legacy-common';
import {
  AppointmentTypeDto,
  AttachmentTypeDto,
  CalendarDto,
  ChargeTicketDiagnosisGroupDto,
  ChargeTicketServicesProvidedGroupDto,
  ClinicalNoteTypeDto,
  ConditionDto,
  FinancialCenterDto,
  GenericFilterDto,
  HistorianDto,
  Lists_AppointmentStatusDto,
  Lists_GenderDto,
  Lists_RaceDto,
  Lists_StateDto,
  MedicationActionDto,
  MedicationCategoryDto,
  MedicationDayDto,
  MedicationFrequencyDto,
  MedicationFundingSourceDto,
  MedicationManufacturerDto,
  MedicationPrnReasonDto,
  MedicationRefillDto,
  MedicationRouteDto,
  MedicationSiteDto,
  MedicationUnitDto,
  MessageTypeDto,
  PhoneTypeDto,
  ProviderDto,
  RxWorkflowStatusDto,
  ServiceSiteDto,
  TimeFrameDto,
  TimespanDto,
  UnitOfMeasureDto,
  UserGroupUserDto,
  VisitFormattingModelDto,
} from 'webcommon/shared';
import {
  ListRepository,
} from 'webcommon/webapi';

@Injectable({
  providedIn: 'root',
})
export class ListService {
  constructor(
    private aprimaCacheService: AprimaCacheService,
    private listRepository: ListRepository,
  ) { }

  private getList(listName: string, params?: any): Observable<any> {
    const cache = this.aprimaCacheService.get('Lists');
    return this.listRepository.getList(listName, params, cache);
  }

  getActiveProblemFilterList(): Observable<GenericFilterDto[]> {
    return this.getList('activeproblemfilter');
  }

  getActiveTimespans(): Observable<TimespanDto[]> {
    return this.getList('activetimespan');
  }

  getAppointmentStatusList(): Observable<Lists_AppointmentStatusDto[]> {
    return this.getList('appointmentstatus');
  }

  getAppointmentTypes(): Observable<AppointmentTypeDto[]> {
    return this.getList('appointmenttype');
  }

  getAttachmentTypes(): Observable<AttachmentTypeDto[]> {
    return this.getList('attachmenttypes');
  }

  getCalendars(): Observable<CalendarDto[]> {
    return this.getList('calendar');
  }

  getClinicalNoteTypes(providerId: string): Observable<ClinicalNoteTypeDto[]> {
    return this.getList('clinicalnotetypes', {
      providerId,
    });
  }

  getConditions(): Observable<ConditionDto[]> {
    return this.getList('patientconditions');
  }

  getDiagnosisGroups(): Observable<ChargeTicketDiagnosisGroupDto[]> {
    return this.getList('diagnoses');
  }

  getFinancialCenters(): Observable<FinancialCenterDto[]> {
    return this.getList('financialcenter');
  }

  getGenders(): Observable<Lists_GenderDto[]> {
    return this.getList('genders');
  }

  getHistorians(): Observable<HistorianDto[]> {
    return this.getList('historians');
  }

  getMedicationsActions(): Observable<MedicationActionDto[]> {
    return this.getList('medications/actions');
  }

  getMedicationsCategories(): Observable<MedicationCategoryDto[]> {
    return this.getList('medications/categories');
  }

  getMedicationsDays(): Observable<MedicationDayDto[]> {
    return this.getList('medications/days');
  }

  getMedicationsFrequencies(): Observable<MedicationFrequencyDto[]> {
    return this.getList('medications/frequencies');
  }

  getMedicationsFundingSources(): Observable<MedicationFundingSourceDto[]> {
    return this.getList('medications/fundingsource');
  }

  getMedicationsManufacturers(): Observable<MedicationManufacturerDto[]> {
    return this.getList('medications/mfr');
  }

  getMedicationsPrnReasons(): Observable<MedicationPrnReasonDto[]> {
    return this.getList('medications/prn-reasons');
  }

  getMedicationsRefills(): Observable<MedicationRefillDto[]> {
    return this.getList('medications/refills');
  }

  getMedicationsRoutes(): Observable<MedicationRouteDto[]> {
    return this.getList('medications/routes');
  }

  getMedicationsSites(): Observable<MedicationSiteDto[]> {
    return this.getList('medications/site');
  }

  getMedicationsUnits(excludeImperial: boolean): Observable<MedicationUnitDto[]> {
    return this.getList('medications/units', {
      excludeImperial,
    });
  }

  getMessageTypes(): Observable<MessageTypeDto[]> {
    return this.getList('messagetypes');
  }

  getPhoneTypes(): Observable<PhoneTypeDto[]> {
    return this.getList('phonetype');
  }

  getProviders(): Observable<ProviderDto[]> {
    return this.getList('provider');
  }

  getRaces(): Observable<Lists_RaceDto[]> {
    return this.getList('racelist');
  }

  getRxWorkflowStatusList(): Observable<RxWorkflowStatusDto[]> {
    return this.getList('rxworkflowstatus');
  }

  getServiceSites(): Observable<ServiceSiteDto[]> {
    return this.getList('servicesite');
  }

  getServicesPerformedGroups(): Observable<ChargeTicketServicesProvidedGroupDto[]> {
    return this.getList('spprocedures');
  }

  getStates(): Observable<Lists_StateDto[]> {
    return this.getList('statelist');
  }

  getTimeFrames(): Observable<TimeFrameDto[]> {
    return this.getList('timeframes');
  }

  getUnitsOfMeasure(excludeImperial: boolean): Observable<UnitOfMeasureDto[]> {
    return this.getList('unitsofmeasure', {
      excludeImperial,
    });
  }

  getUserGroupUsers(): Observable<UserGroupUserDto[]> {
    return this.getList('users');
  }

  getVisitFormattingModels(): Observable<VisitFormattingModelDto[]> {
    return this.getList('visitformattingmodels');
  }
}
